import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { HighlightedQuote } from '../components/ui/HighlightedQuote';
import { TestimonialCard } from '../components/ui/TestimonialCard';
import { ProductShowcase } from '../components/ui/ProductShowcase';
import { StickyCTABar } from '../components/ui/StickyCTABar';
import { CTAButton } from '../components/ui/CTAButton';
export function AdvertorialPage() {
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-50px'
    },
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  };
  return <div className="min-h-screen bg-[#FDFBF7] text-gray-800 font-serif selection:bg-[#7C9885]/20 selection:text-gray-900" data-id="element-39">
      {/* Navigation / Header - Minimal Editorial Style */}
      <header className="border-b border-gray-100 bg-[#FDFBF7]/95 backdrop-blur-sm py-6 sticky top-0 z-40 transition-all duration-300" data-id="element-40">
        <div className="max-w-[680px] mx-auto px-5 md:px-8 flex justify-between items-center" data-id="element-41">
          <div className="font-serif font-bold text-xl tracking-tight text-gray-900" data-id="element-42">
            THE VEGAN JOURNAL
          </div>
        </div>
      </header>

      <main className="max-w-[680px] mx-auto px-5 md:px-8 py-12 md:py-20 lg:py-28" data-id="element-43">
        {/* Hero Section */}
        <article className="prose prose-lg prose-stone max-w-none prose-p:font-serif prose-headings:font-serif" data-id="element-44">
          <motion.div {...fadeIn} className="mb-14 md:mb-20 text-center" data-id="element-45">
            <span className="inline-block px-3 py-1 mb-6 text-xs font-medium tracking-widest text-[#7C9885] uppercase border border-[#7C9885]/30 rounded-full font-sans" data-id="element-46">
              Personal Story
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-serif font-bold leading-[1.1] mb-6 md:mb-8 text-gray-900 tracking-tight" data-id="element-47">
              I Finally Found What Was Missing After 7 Years of Brain Fog
            </h1>
          </motion.div>

          {/* Author Byline */}
          <div className="flex items-center justify-center gap-4 mb-14 md:mb-20 font-sans text-sm text-gray-500 border-y border-gray-100 py-6" data-id="element-48">
            <div className="flex items-center gap-3" data-id="element-49">
              <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden" data-id="element-50">
                <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=100" alt="Sarah Jenkins" className="w-full h-full object-cover" loading="eager" decoding="async" width="100" height="100" data-id="element-51" />
              </div>
              <span data-id="element-52">
                By{' '}
                <span className="font-semibold text-gray-900 underline decoration-gray-200 underline-offset-4" data-id="element-53">
                  Sarah Jenkins
                </span>
              </span>
            </div>
            <span className="text-gray-300" data-id="element-54">•</span>
            <span data-id="element-55">7 min read</span>
          </div>

          {/* PLACEHOLDER 1: HERO - Internal conflict at restaurant */}
          <motion.figure {...fadeIn} className="my-14 md:my-20 -mx-5 md:-mx-10" data-id="element-56">
            <div className="relative aspect-[16/9] overflow-hidden rounded-sm bg-gray-100 border border-gray-200" data-id="element-57">
              <img src="https://cdn.magicpatterns.com/uploads/6GTkSWmyxBhqHqTsSdwWPJ/Gemini_Generated_Image_mkfdu7mkfdu7mkfd.png" alt="Person experiencing internal conflict at restaurant" className="w-full h-full object-cover" loading="eager" decoding="async" width="1600" height="900" data-id="element-58" />
            </div>
            <figcaption className="text-center text-xs text-gray-400 mt-4 font-sans italic" data-id="element-59">
              The moment everything almost changed.
            </figcaption>
          </motion.figure>

          {/* Main Content - OPENING CONFESSIONAL (TEXT ONLY) */}
          <div className="space-y-8 md:space-y-10 text-[1.25rem] leading-[1.7] text-gray-800 font-serif" data-id="element-60">
            <p className="first-letter:text-5xl md:first-letter:text-6xl first-letter:font-bold first-letter:mr-3 first-letter:float-left first-letter:text-[#7C9885] first-letter:leading-[0.8]" data-id="element-61">
              I need to tell you how I found a way out.
            </p>
            <p data-id="element-62">
              Not out of veganism. Out of the fog. Out of feeling like my brain
              was broken. Out of the quiet panic that had been building for
              months.
            </p>
            <p data-id="element-63">
              If you're reading this, you probably know what I mean. That
              creeping fear that something is wrong — really wrong — and none of
              the usual fixes are working.
            </p>
            <p data-id="element-64">
              I'm going to tell you the whole story. The bad parts. The
              embarrassing parts. The part I've never told anyone.
            </p>
            <p className="font-semibold text-gray-900" data-id="element-65">
              And then I'm going to tell you what I finally found.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold mb-6 mt-16 md:mt-20 text-gray-900 font-sans tracking-tight" data-id="element-66">
              The Brain That Wouldn't Work
            </h2>

            {/* PLACEHOLDER 2: BRAIN FOG / COGNITIVE STRAIN */}
            <motion.figure {...fadeIn} className="my-14 md:my-20 -mx-5 md:-mx-10" data-id="element-67">
              <div className="relative aspect-[16/9] overflow-hidden rounded-sm bg-gray-100 border border-gray-200" data-id="element-68">
                <img src="https://cdn.magicpatterns.com/uploads/c3yawJgWr3EFyVETnSBdoG/ChatGPT_Image_Jan_23,_2026,_12_28_29_AM.png" alt="Person experiencing brain fog while working on laptop" className="w-full h-full object-cover" loading="lazy" decoding="async" width="1600" height="900" data-id="element-69" />
              </div>
            </motion.figure>

            <p data-id="element-70">
              Six months before I figured this out, my brain started failing me.
            </p>
            <p data-id="element-71">
              Not in a dramatic way at first. Small things. I'd be
              mid-conversation and completely lose the word I was reaching for.
              Not a complicated word. Something basic. I'd just... trail off.
            </p>
            <p data-id="element-72">
              I started rereading the same email five times and still not
              absorbing it. I'd get to the end and realize I had no idea what it
              said.
            </p>
            <p data-id="element-73">Then the fog settled in.</p>
            <p data-id="element-74">
              Not tiredness. I know tiredness. This was different. This was
              sitting at my desk at 2pm, staring at my screen, feeling like I
              was thinking through mud. Like someone had turned down the
              contrast on my mind.
            </p>

            <HighlightedQuote data-id="element-75">
              My brain just doesn't work the way it used to.
            </HighlightedQuote>

            <p data-id="element-76">
              That thought started creeping in around month two. By month four,
              I couldn't ignore it anymore.
            </p>
            <p data-id="element-77">
              The worst part was the feeling of watching myself from the
              outside. Going through the motions of my day. Functioning, but
              barely. Present in meetings but not really there.
            </p>
            <p data-id="element-78">
              I used to be sharp. Quick. The person who remembered everything,
              caught the details others missed, made connections fast.
            </p>
            <p data-id="element-79">Now I felt slow. Genuinely slow. It was embarrassing.</p>
            <p data-id="element-80">
              I'd forget words mid-sentence and just trail off. I'd walk into
              rooms and have no idea why I was there. I'd call someone's name —
              someone I'd known for years — and draw a complete blank.
            </p>

            <p className="font-medium text-xl text-gray-900 bg-[#faf8f5] px-4 py-2 inline-block rounded-sm border-l-2 border-[#7C9885]/30" data-id="element-81">
              It feels like part of my brain is missing.
            </p>

            <p data-id="element-82">That's the only way I could describe it.</p>

            <h2 className="text-2xl md:text-3xl font-bold mb-6 mt-16 md:mt-20 text-gray-900 font-sans tracking-tight" data-id="element-83">
              "You're Doing Everything Right"
            </h2>

            <p data-id="element-84">The maddening part? On paper, I was the model vegan.</p>
            <p data-id="element-85">
              Seven years in. I'd optimized everything. Tracked my meals. Hit my
              protein targets. Ate the rainbow of vegetables every day.
            </p>
            <p data-id="element-86">
              I took B12. Iron. D3. Omega-3s from algae. Magnesium. A vegan
              multivitamin. I had a cabinet full of supplements that could stock
              a small health food store.
            </p>
            <p data-id="element-87">
              I went to the doctor. Got blood tests. Everything came back...
              normal.
            </p>
            <p data-id="element-88">"Your levels look fine," they said. "Maybe it's stress?"</p>

            <p className="font-semibold text-gray-900 italic" data-id="element-89">
              My blood tests are normal. But I'm not.
            </p>

            <p data-id="element-90">
              I heard variations of this three different times. From three
              different doctors. They'd look at the numbers, shrug, and suggest
              I get more sleep or try meditation.
            </p>
            <p data-id="element-91">
              I was sleeping eight hours. I was meditating. I was doing
              everything the forums said. Everything the dietitians said.
              Everything the vegan wellness influencers said.
            </p>
            <p data-id="element-92">And I still felt like my brain was shutting down.</p>

            {/* PLACEHOLDER 3: SUPPLEMENTS / FRUSTRATION */}
            <motion.figure {...fadeIn} className="my-14 md:my-20 -mx-5 md:-mx-10" data-id="element-93">
              <div className="relative aspect-[16/9] overflow-hidden rounded-sm bg-gray-100 border border-gray-200" data-id="element-94">
                <img src="https://cdn.magicpatterns.com/uploads/d9tFHHJPxFVDB6QEgXXi6n/Sups.png" alt="Various supplement bottles representing frustration with ineffective solutions" className="w-full h-full object-cover" loading="lazy" decoding="async" width="1600" height="900" data-id="element-95" />
              </div>
            </motion.figure>

            <p className="font-medium text-xl text-gray-900 bg-[#faf8f5] px-4 py-2 inline-block rounded-sm border-l-2 border-[#7C9885]/30" data-id="element-96">
              I've tried everything and nothing works.
            </p>

            <p data-id="element-97">
              I spent hundreds on supplements that did nothing. Adaptogens.
              Nootropics. Mushroom coffees. More B12. Different forms of iron.
              Every "brain fog cure" that popped up in my feed.
            </p>
            <p data-id="element-98">
              I tracked my food obsessively, thinking maybe I was missing
              something. More protein? Different greens? More variety?
            </p>
            <p data-id="element-99">
              It's exhausting having to constantly figure out what you're
              missing.
            </p>
            <p data-id="element-100">
              I was guessing. Just throwing darts at a wall of supplements,
              hoping something would stick.
            </p>
            <p data-id="element-101">Nothing did.</p>

            <h2 className="text-2xl md:text-3xl font-bold mb-6 mt-16 md:mt-20 text-gray-900 font-sans tracking-tight" data-id="element-102">
              The Thought I Couldn't Say Out Loud
            </h2>

            {/* TEXT ONLY SECTION - NO IMAGES */}
            <p data-id="element-103">Here's where this story gets harder to tell.</p>
            <p data-id="element-104">
              Everything I was experiencing pointed to one conclusion I couldn't
              accept:
            </p>
            <p className="font-semibold text-gray-900" data-id="element-105">
              Maybe my body just couldn't handle being vegan anymore.
            </p>
            <p data-id="element-106">
              I didn't say it out loud. I barely let myself think it. But the
              thought was there. Growing. Getting louder.
            </p>

            <HighlightedQuote data-id="element-107">
              I feel like I'm choosing between my health and my values.
            </HighlightedQuote>

            <p data-id="element-108">
              Veganism wasn't just a diet for me. It was part of who I am. The
              person I'd built over seven years. The friend who brought her own
              food to barbecues. Who cried during Dominion. Who'd convinced
              three people to go plant-based.
            </p>
            <p data-id="element-109">If I quit veganism, what does that say about me?</p>
            <p data-id="element-110">
              I couldn't imagine the conversation with my vegan friends. The "I
              told you so" from my family. The comments from people who'd been
              waiting for me to fail.
            </p>
            <p className="font-semibold text-gray-900" data-id="element-111">I feel trapped.</p>
            <p data-id="element-112">
              That's the only way to describe it. Trapped between a body that
              was failing and a belief system I wasn't willing to abandon.
            </p>
            <p data-id="element-113">So I started doing something I'm ashamed to admit.</p>
            <p data-id="element-114">
              Late at night, I'd search Reddit. Incognito mode. Looking for
              answers from ex-vegans. From people who'd felt what I was feeling.
            </p>
            <p data-id="element-115">And I kept seeing the same thing.</p>

            <h2 className="text-2xl md:text-3xl font-bold mb-6 mt-16 md:mt-20 text-gray-900 font-sans tracking-tight" data-id="element-116">
              The Pattern I Couldn't Ignore
            </h2>

            {/* TEXT ONLY - NO IMAGES IN REDDIT SECTION */}
            <p data-id="element-117">
              In the quiet corners of the internet, I noticed something
              disturbing.
            </p>
            <p data-id="element-118">Vegans who ate meat after years kept saying the same thing:</p>

            <div className="bg-[#faf8f5] p-8 md:p-10 rounded-sm border-l-4 border-[#7C9885]/20 my-10 italic text-gray-700 space-y-6 font-serif text-xl leading-relaxed" data-id="element-119">
              <p className="flex gap-3" data-id="element-120">
                <span className="text-[#7C9885] font-bold" data-id="element-121">"</span>I felt better
                almost immediately.
              </p>
              <p className="flex gap-3" data-id="element-122">
                <span className="text-[#7C9885] font-bold" data-id="element-123">"</span>Within days,
                my brain fog lifted.
              </p>
              <p className="flex gap-3" data-id="element-124">
                <span className="text-[#7C9885] font-bold" data-id="element-125">"</span>I hate
                admitting it, but I felt like myself again.
              </p>
            </div>

            <p className="font-medium text-xl text-gray-900 bg-[#faf8f5] px-4 py-2 inline-block rounded-sm border-l-2 border-[#7C9885]/30" data-id="element-126">
              Every ex-vegan says the same thing — they felt better within days.
            </p>

            <p data-id="element-127">
              I read those posts with a mixture of horror and hunger. Story
              after story of people who'd suffered for months or years, then ate
              a steak or some fish and suddenly — suddenly — felt like the
              lights came back on.
            </p>

            <motion.figure {...fadeIn} className="my-10 md:my-14" data-id="element-128">
              <div className="relative overflow-hidden rounded-lg shadow-md border border-gray-200" data-id="element-129">
                <img src="https://cdn.magicpatterns.com/uploads/sszX2GzNFqSSdiuoawMvT2/www.reddit.com_r_vegan_comments_1o8mzui_for_those_who_say_they_feel_better_after__(1).png" alt="Reddit post from r/vegan about feeling better after eating meat" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-130" />
              </div>
            </motion.figure>

            <p data-id="element-131">It scares me how fast people say they feel better.</p>
            <p data-id="element-132">
              Because I didn't want to believe it. I didn't want that to be the
              answer.
            </p>
            <p data-id="element-133">
              But the pattern was undeniable. Something in meat was doing
              something that nothing in my supplement cabinet could replicate.
            </p>
            <p className="font-semibold text-gray-900" data-id="element-134">
              What if they're right?
            </p>
            <p data-id="element-135">That thought kept me up at night.</p>

            <h2 className="text-2xl md:text-3xl font-bold mb-6 mt-16 md:mt-20 text-gray-900 font-sans tracking-tight" data-id="element-136">
              47 Minutes
            </h2>

            {/* PLACEHOLDER 4: STEAK MOMENT - ONLY PLACE FOOD IS IMPLIED */}
            <motion.figure {...fadeIn} className="my-14 md:my-20 -mx-5 md:-mx-10" data-id="element-137">
              <div className="relative aspect-[16/9] overflow-hidden rounded-sm bg-gray-100 border border-gray-200" data-id="element-138">
                <img src="https://cdn.magicpatterns.com/uploads/wKK8TAansAKHAfvTC6J2eh/Steak.png" alt="Steak dinner representing the moment of temptation" className="w-full h-full object-cover" loading="lazy" decoding="async" width="1600" height="900" data-id="element-139" />
              </div>
            </motion.figure>

            <p data-id="element-140">Which brings me to the worst night of my life as a vegan.</p>
            <p data-id="element-141">
              I was at a restaurant. Alone. I'd been staring at the menu for
              what felt like forever.
            </p>
            <p data-id="element-142">
              47 minutes. That's how long I sat there while a war raged inside
              me.
            </p>

            <p className="font-medium text-xl text-gray-900 bg-[#faf8f5] px-4 py-2 inline-block rounded-sm border-l-2 border-[#7C9885]/30" data-id="element-143">
              I don't want to eat meat. I just want my brain back.
            </p>

            <p data-id="element-144">
              I stared at the steak section. I could order it. Right now. No one
              would know. I could eat it, feel better, and figure out what that
              meant later.
            </p>
            <p data-id="element-145">My finger was hovering over the menu.</p>
            <p data-id="element-146">Then my phone buzzed.</p>

            <h2 className="text-2xl md:text-3xl font-bold mb-6 mt-16 md:mt-20 text-gray-900 font-sans tracking-tight" data-id="element-147">
              The Notification That Changed Everything
            </h2>

            <p data-id="element-148">
              A reply to a post I'd made weeks earlier. A desperate 2am post on
              a vegan health forum about brain fog. Someone I'd never met had
              finally responded:
            </p>

            <motion.figure {...fadeIn} className="my-14 md:my-20" data-id="element-149">
              <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm border border-gray-100 max-w-md mx-auto transform rotate-1" data-id="element-150">
                <div className="flex items-center gap-3 mb-4" data-id="element-151">
                  <div className="w-8 h-8 rounded-full bg-[#7C9885] flex items-center justify-center text-white font-bold text-xs" data-id="element-152">
                    R
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-153">
                    <span className="font-bold text-gray-900" data-id="element-154">
                      vegan_runner
                    </span>{' '}
                    • 2h ago
                  </div>
                </div>
                <p className="text-base md:text-lg font-sans leading-relaxed text-gray-800" data-id="element-155">
                  "Have you tried creatine? It's not just for bodybuilders. Our
                  brains need it too, and we get zero from plants. Changed my
                  life."
                </p>
              </div>
              <figcaption className="text-xs md:text-sm text-gray-400 mt-4 italic text-center font-sans" data-id="element-156">
                The notification that changed everything.
              </figcaption>
            </motion.figure>

            <p data-id="element-157">I put the menu down. I ordered the vegetable curry.</p>
            <p data-id="element-158">
              And when I got home, I stayed up until 4am reading everything I
              could find.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold mb-6 mt-16 md:mt-20 text-gray-900 font-sans tracking-tight" data-id="element-159">
              Here's What Nobody Told Us
            </h2>

            <p className="font-bold text-xl text-gray-900" data-id="element-160">Creatine.</p>

            <p data-id="element-161">
              Your body makes some. But the rest is supposed to come from your
              diet — from meat and fish. Sources that don't exist in a
              plant-based diet.
            </p>
            <p data-id="element-162">
              Which means for seven years, I'd been running my brain on a
              depleted tank.
            </p>
            <p data-id="element-163">
              Think of it like a phone battery. Your brain needs fast energy to
              focus, remember, and push through mental work. Creatine is what
              lets it recharge quickly between demands. Without enough of it,
              you're constantly running at 15%.
            </p>

            <motion.figure {...fadeIn} className="my-14 md:my-20" data-id="element-164">
              <div className="max-w-lg mx-auto" data-id="element-165">
                <div className="relative overflow-hidden rounded-sm shadow-sm border border-gray-100" data-id="element-166">
                  <img src="https://cdn.magicpatterns.com/uploads/66Q4knEctGEYCeAdBQhoeY/Creatine_availability.png" alt="Chart showing creatine availability in vegetarians, vegans, and omnivores" className="w-full h-auto object-contain mix-blend-multiply" loading="lazy" decoding="async" width="800" height="600" data-id="element-167" />
                </div>
              </div>
            </motion.figure>

            <p data-id="element-168">That's why those ex-vegans felt better within days.</p>
            <p data-id="element-169">
              Not because their bodies "needed meat." Because their bodies were
              starving for creatine — and meat is the fastest way to get it.
            </p>

            <p className="font-medium text-xl text-gray-900 bg-[#faf8f5] px-4 py-2 inline-block rounded-sm border-l-2 border-[#7C9885]/30" data-id="element-170">
              You don't need the animal. You need the molecule.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold mb-6 mt-16 md:mt-20 text-gray-900 font-sans tracking-tight" data-id="element-171">
              The Solution (That Let Me Stay Who I Am)
            </h2>

            <p data-id="element-172">I found a company called Loomy.</p>
            <p data-id="element-173">
              They make creatine gummies specifically for vegans and
              vegetarians. Not gym-bro branding. Not chalky powder to mix. Just
              a simple daily gummy designed for people like me — people who
              needed their brains back without betraying their values.
            </p>
            <p data-id="element-174">I ordered it that night.</p>
            <p data-id="element-175">Here's what happened.</p>

            {/* LOOMY PRODUCT IMAGE - KEEP AS IS */}
            <motion.figure {...fadeIn} className="my-14 md:my-20" data-id="element-176">
              <div className="aspect-square w-full md:max-w-sm md:mx-auto rounded-lg overflow-hidden shadow-sm" data-id="element-177">
                <img src="https://cdn.shopify.com/s/files/1/0936/8453/1542/files/Untitled_design_39.png?v=1769139568" alt="Hand holding Loomy Creatine Gummies package" className="w-full h-full object-cover" loading="lazy" decoding="async" width="800" height="800" data-id="element-178" />
              </div>
            </motion.figure>

            <div className="space-y-8 md:space-y-10" data-id="element-179">
              <p data-id="element-180">
                <span className="font-bold text-gray-900" data-id="element-181">First few days:</span>{' '}
                Honestly? Nothing. I didn't feel different. I wondered if I'd
                fallen for another supplement that would end up abandoned in my
                cabinet.
              </p>

              <p data-id="element-182">
                <span className="font-bold text-gray-900" data-id="element-183">
                  Day five or six:
                </span>{' '}
                Something small. I woke up and didn't immediately feel like I
                needed more sleep. It was subtle enough that I almost didn't
                notice.
              </p>

              <p data-id="element-184">
                <span className="font-bold text-gray-900" data-id="element-185">Week two:</span> The
                fog started lifting. I wasn't searching for words anymore. I
                could read an email once and actually understand it. My brain
                just... worked.
              </p>

              <p data-id="element-186">
                <span className="font-bold text-gray-900" data-id="element-187">Week three:</span> My
                energy came back. Not a caffeine spike. Not that jittery,
                artificial feeling. Just a steady, normal energy I'd forgotten I
                used to have.
              </p>

              <p data-id="element-188">
                By the{' '}
                <span className="font-bold text-gray-900" data-id="element-189">
                  end of the first month:
                </span>
              </p>

              <p className="font-medium text-2xl md:text-3xl text-gray-900 border-l-[6px] border-[#7C9885] pl-8 py-4 my-10" data-id="element-190">
                I felt like myself again.
              </p>

              <p data-id="element-191">
                For the first time in over a year, I felt like the person I used
                to be. Sharp. Quick. Present. Actually there instead of watching
                my life through a fog.
              </p>
              <p className="font-semibold text-gray-900" data-id="element-192">
                And I'd done it without eating a single animal product.
              </p>
            </div>

            <ProductShowcase data-id="element-193" />

            <div className="my-16 md:my-24" data-id="element-194">
              <h3 className="text-xl md:text-2xl font-bold mb-10 text-gray-900 font-sans tracking-tight text-center" data-id="element-195">
                What Others Are Saying
              </h3>
              <div className="grid md:grid-cols-2 gap-8 md:gap-12" data-id="element-196">
                <TestimonialCard quote="I was three weeks away from adding fish back into my diet. Loomy stopped me from having to make that choice. I can't explain how much that means." author="Sarah" role="Vegan 5 years" imageSrc="https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=100" data-id="element-197" />
                <TestimonialCard quote="I cried when it worked. Because I'd genuinely believed I was going to have to choose between my ethics and my brain. And I didn't." author="Emma" role="Vegan 6 years" imageSrc="https://images.unsplash.com/photo-1544725176-7c40e5a71c5e?auto=format&fit=crop&q=80&w=100" data-id="element-198" />
                <TestimonialCard quote="I did everything right for years and still felt broken. This was the one thing I'd never tried because I thought it was just for bodybuilders. I wish I'd found it sooner." author="Rachel" role="Vegan 8 years" imageSrc="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=100" data-id="element-199" />
              </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-lg p-8 md:p-12 my-16 shadow-sm" data-id="element-200">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 font-sans tracking-tight" data-id="element-201">
                You Don't Have to Break
              </h2>
              <div className="space-y-6 text-[1.25rem] leading-[1.7]" data-id="element-202">
                <p className="text-gray-800" data-id="element-203">
                  I don't know where you are right now.
                </p>
                <p className="text-gray-700" data-id="element-204">
                  Maybe you're in the restaurant. Menu open. 47 minutes in.
                  Wondering if tonight's the night you finally give up.
                </p>
                <p className="text-gray-700" data-id="element-205">
                  Maybe you're still months away from that moment, feeling
                  yourself getting closer.
                </p>
                <p className="text-gray-700" data-id="element-206">
                  Maybe you've already slipped, and you're carrying the guilt of
                  it.
                </p>
                <p className="font-semibold text-gray-900" data-id="element-207">
                  Wherever you are: you don't have to break to feel better.
                </p>
                <p className="text-gray-700" data-id="element-208">
                  There's a way to get what meat provides without eating it. I
                  found it. Now you know it exists.
                </p>
                <p className="text-gray-700" data-id="element-209">
                  You're not failing veganism. You were just missing something
                  nobody told you about.
                </p>
              </div>

              <div className="mt-10 pt-10 border-t border-gray-100 space-y-4" data-id="element-210">
                <p className="text-xl md:text-2xl font-bold text-gray-900 font-serif" data-id="element-211">
                  If I can replace what meat fixes fast, I don't have to quit.
                </p>
                <p className="text-lg md:text-xl text-gray-600 font-sans" data-id="element-212">
                  That's what I learned. That's what I want you to know.
                </p>
              </div>

              <div className="mt-12 text-center" data-id="element-213">
                <CTAButton size="lg" className="mx-auto shadow-md hover:shadow-lg w-full md:w-auto px-10 py-4" data-id="element-214">
                  Try Loomy
                </CTAButton>
              </div>
            </div>
          </div>
        </article>
      </main>

      <footer className="bg-white py-16 md:py-24 border-t border-gray-100" data-id="element-215">
        <div className="max-w-[680px] mx-auto px-5 md:px-8 text-center text-gray-400 text-sm" data-id="element-216">
          <div className="mb-10" data-id="element-217">
            <span className="font-serif font-bold text-xl tracking-tight text-gray-900" data-id="element-218">
              THE VEGAN JOURNAL
            </span>
          </div>
          <p className="mb-8 font-sans" data-id="element-219">
            © {new Date().getFullYear()} Loomy. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-10 text-xs font-medium tracking-wide uppercase" data-id="element-220">
            <a href="#" className="hover:text-gray-900 transition-colors" data-id="element-221">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-gray-900 transition-colors" data-id="element-222">
              Terms of Service
            </a>
            <a href="#" className="hover:text-gray-900 transition-colors" data-id="element-223">
              Contact Us
            </a>
          </div>
          <p className="max-w-xl mx-auto text-[11px] text-gray-300 px-4 leading-relaxed font-sans" data-id="element-224">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease. The information provided is for
            informational purposes only and is not intended as a substitute for
            advice from your physician or other health care professional.
          </p>
        </div>
      </footer>

      <StickyCTABar data-id="element-225" />
    </div>;
}